import { useEffect, useState } from "react";
import fetchStatus from "src/utils/api/fetch-status";
import { useActiveDate } from "src/scenes/dashboard/date-provider";
import { addDays, format } from "date-fns";
import { useActiveIntegrationsTeams } from "src/scenes/teams/dashboard/active-integrations-provider-teams";
import { useActiveIntegrations } from "src/scenes/dashboard/active-integrations-provider";
import useAbortableStreamFetch from "src/utils/request/use-abortable-stream-fetch";
import type {
    TemplateDataSettings,
    TemplateDataSettingsDateRange,
} from "src/hooks/use-template-data-api/types";
import { useAuth } from "src/components/providers/auth-context";

const useTemplateApi = ({
    requestMethodMock,
    requiredIntegrations,
    url,
    body,
    isTeams,
    isSample,
}: {
    requestMethodMock: (_body: TemplateDataSettings) => unknown;
    requiredIntegrations?: string[];
    url: string;
    body: Omit<TemplateDataSettings, "dateRange">;
    isTeams: boolean;
    isSample: boolean;
}) => {
    const { fromDate, toDate } = useActiveDate();
    const { activeIntegrations } = (isTeams ? useActiveIntegrationsTeams : useActiveIntegrations)();
    const { isDemo, userHasTeamsSubscription } = useAuth();

    const [state, setState] = useState<{
        status: fetchStatus;
        data: null | object;
        errorCode: null | number;
        string: "";
        controller: AbortController | null;
    }>({
        status: fetchStatus.PENDING,
        data: null,
        errorCode: null,
        string: "",
        controller: null,
    });

    const getDateRange = (): TemplateDataSettingsDateRange => {
        if (body.sprintDateRange) {
            return { dateRange: body.sprintDateRange };
        }
        if (fromDate && toDate) {
            return {
                dateRange: {
                    fromDate: format(fromDate, "yyyy-MM-dd"),
                    toDate: format(addDays(toDate, 1), "yyyy-MM-dd"),
                },
            };
        }
        return { defaultDateRange: body.defaultDateRange };
    };

    const fetchMock = async () => {
        try {
            const { sprintDateRange: _, ...bodyForRequest } = body;
            const fullBody: TemplateDataSettings = {
                ...bodyForRequest,
                ...getDateRange(),
            } as TemplateDataSettings;
            const response = await requestMethodMock(fullBody);
            // @ts-expect-error response is unknown here
            setState((prevState) => ({
                ...prevState,
                status: fetchStatus.SUCCESS,
                data: response,
                errorCode: null,
                controller: null,
            }));
        } catch (e) {
            setState((prevState) => ({
                ...prevState,
                status: fetchStatus.ERROR,
                data: null,
                errorCode: e.status,
                controller: null,
            }));
        }
    };

    // eslint-disable-next-line consistent-return
    const fetchEffect = (controller) => {
        const actualController = controller || new AbortController();
        setState({
            status: fetchStatus.PENDING,
            data: null,
            errorCode: null,
            string: "",
            controller: actualController,
        });

        if (process.env.MOCK_WORK === "true") return fetchMock();
        if (isDemo || isSample || (isTeams && !userHasTeamsSubscription)) return fetchMock();
        if (activeIntegrations.status === fetchStatus.SUCCESS) {
            if (!activeIntegrations.data.integrations.find((int) => int.connected))
                return fetchMock();
            if (!isTeams && requiredIntegrations) {
                const shouldRequest = !!activeIntegrations.data.integrations.find(
                    (int) => int.connected && requiredIntegrations.includes(int.name)
                );
                if (!shouldRequest) return undefined;
            }
            if (activeIntegrations.data.integrations.find((int) => int.connected)) {
                setState((prevState) => ({
                    ...prevState,
                    data: null,
                    string: "",
                }));
                const { sprintDateRange: _, ...bodyForRequest } = body;
                const fullBody: TemplateDataSettings = {
                    ...bodyForRequest,
                    ...getDateRange(),
                } as TemplateDataSettings;
                useAbortableStreamFetch(url, state, actualController, setState, fromDate, toDate, {
                    method: "POST",
                    body: JSON.stringify(fullBody),
                });
            } else {
                fetchMock();
            }
        }
    };

    const refetch = (controller) => {
        if (state.controller) {
            state.controller.abort("unmounted");
        }
        setState((prevState) => ({
            ...prevState,
            status: fetchStatus.REFETCHING,
            controller,
        }));
        return fetchEffect(controller);
    };

    useEffect(() => {
        const controller = new AbortController();
        if (body && body.queries && body.queries.length) {
            refetch(controller);
        }
        return () => {
            if (controller) {
                controller.abort("unmounted");
            }
        };
    }, [
        activeIntegrations &&
            activeIntegrations.data &&
            activeIntegrations.data.integrations &&
            JSON.stringify(activeIntegrations.data.integrations),
        fromDate,
        toDate,
        url,
        JSON.stringify(body),
        isTeams,
    ]);

    return {
        ...state,
        refetch,
    };
};

export default useTemplateApi;
